import React from 'react';
import PropTypes from 'prop-types';
import {
	getNavItemKey,
	isVisibleNavItem,
	isFragmentWrapper,
	narrowZonePosition,
	resolveZonePosition,
	safeNavItems
} from '../utils/navItemUtils';
import { ZONE_POSITION } from '../utils/trackEvent';
import DrilldownLeafLink from './DrilldownLeafLink';
import DrawerNavFragment from './DrawerNavFragment';
import NavItemLabel from './NavItemLabel';

/** BEM class for the drilldown section heading element. */
export const DRILLDOWN_HEADING_CLASS =
	'ws-nav-drawer__drilldown-section-heading';

/**
 * DrilldownGroup — renders a group heading followed by its visible child links
 * and any embedded fragment slots.
 *
 * @param {Object} props
 * @param {NavigationItem} props.group - A navigation item acting as a group heading
 * @param {() => void} props.onLinkClick - Called when a child link is clicked
 * @param {'above'|'below'} [props.zonePosition] - Where this whole group sits
 *   relative to the nav links of the list that renders it, resolved by that list.
 *   Needed because a group's embedded zones are not automatically `below`: a
 *   fragment *wrapper* has no links of its own (every child is embedded, so the
 *   <ul> below never renders), and one sitting first in a drilldown is genuinely
 *   `above`. Defaults to BELOW, the conservative value.
 */
const DrilldownGroup = ({
	group,
	onLinkClick,
	zonePosition = ZONE_POSITION.BELOW
}) => {
	const allChildren = safeNavItems(group).filter(
		(child) =>
			isVisibleNavItem(child) ||
			child.embedded ||
			isFragmentWrapper(child)
	);
	if (allChildren.length === 0) return null;

	// Pass 1: non-embedded children (leaf links + fragment-wrapper groups) → inside <ul>
	const nonEmbeddedChildren = allChildren.filter((child) => !child.embedded);
	// Pass 2: embedded children (fragment slots) → <div> elements AFTER the <ul>
	const embeddedChildren = allChildren.filter((child) => child.embedded);

	// Every link this group owns is in `nonEmbeddedChildren`, and the embedded
	// zones render after all of them — so resolving at the end of that list
	// answers "does any link precede these zones inside this group?". Combined
	// with the inherited position because document order spans both lists.
	const embeddedZonePosition = narrowZonePosition(
		zonePosition,
		resolveZonePosition(nonEmbeddedChildren, nonEmbeddedChildren.length)
	);

	return (
		<li className="ws-nav-drawer__drilldown-group">
			{group.label?.trim() && (
				<h3
					className={`${DRILLDOWN_HEADING_CLASS} pt-8 pb-4 h4 mt-0 mb-0`}
					data-testid="drilldown-section-heading"
				>
					<NavItemLabel label={group.label} />
				</h3>
			)}
			{nonEmbeddedChildren.length > 0 && (
				<ul className="list-unstyled m-0">
					{nonEmbeddedChildren.map((child, index) =>
						isFragmentWrapper(child) ? (
							<DrilldownGroup
								key={getNavItemKey(child, index)}
								group={child}
								onLinkClick={onLinkClick}
								zonePosition={narrowZonePosition(
									zonePosition,
									resolveZonePosition(
										nonEmbeddedChildren,
										index
									)
								)}
							/>
						) : (
							<DrilldownLeafLink
								key={getNavItemKey(child, index)}
								item={child}
								onLinkClick={onLinkClick}
							/>
						)
					)}
				</ul>
			)}
			{embeddedChildren.map((child, index) => (
				<div
					key={getNavItemKey(child, index)}
					className="ws-nav-drawer__fragment"
				>
					<DrawerNavFragment
						url={child.url}
						zonePosition={embeddedZonePosition}
					/>
				</div>
			))}
		</li>
	);
};

DrilldownGroup.propTypes = {
	group: PropTypes.shape({
		label: PropTypes.string,
		navigationItems: PropTypes.arrayOf(PropTypes.shape({}))
	}).isRequired,
	onLinkClick: PropTypes.func.isRequired,
	zonePosition: PropTypes.oneOf(Object.values(ZONE_POSITION))
};

export default DrilldownGroup;
